/**
 * Krok UXR3 — OBLASTI PROVĚŘENÍ (zadani bod 5, predloha `Zakazka.dc.html`
 * sekce „Prověření").
 *
 * Nalez R.B. 2. 10. 2026 23:34: detail zakazky mluvi internim jazykem —
 * „0 ze 4 povinných bran", „nezjištěno". Predloha misto toho ukazuje SEZNAM
 * OBLASTI: kazda je bud „ověřeno" (zeleny hacek + odkud to vime), nebo
 * „chybí ověřit" (prerusovany kruh) a nese JEDNU akci — „Dohledat", kdyz si
 * to REOS umi dotahnout sam, jinak „Zadat".
 *
 * ---------------------------------------------------------------------------
 * ROZHODNUTI UXR3-OBLASTI — odkud se seznam bere
 *
 * Seznam je SJEDNOCENI dvou mnozin, obou ze serveru:
 *  a) POVINNE oblasti pro zjisteny typ nemovitosti
 *     (`MANDATORY_GATES_BY_CASE_KIND`) — ty musi byt videt vzdy, i kdyz o
 *     nich beh nic nerekl,
 *  b) oblasti, ktere beh skutecne vyhodnotil (`fixture.gates`) — tam patri i
 *     nepovinne (vecna bremena, zastavy, kapacita siti, uzemni plan).
 *
 * Proc sjednoceni a ne jen (b): seznam bran ze serveru je v produkci
 * PRAZDNY (`persist.ts` plni `claimType` hodnotou `dd_facts.fact_key`, takze
 * `evaluateGate` vraci `null` — NALEZ 1 kroku UX1, podrobne v hlavicce
 * `case-gates.ts`). Kdyby se seznam bral jen z nej, sekce „Prověření" by u
 * ostre zakazky byla PRAZDNA a obrazovka by tvrdila, ze neni co proverovat.
 *
 * Pocty („2 ze 6 ověřeno") pocita `gateProgress` — TENTYZ vypocet, jaky
 * pouziva prouzek fazi (`case-phases.ts`) a hlavicka. Jedno cislo na jedne
 * obrazovce (nalez AUD1 28. 9. 2026).
 * ---------------------------------------------------------------------------
 */

import { t } from '@reos/i18n';
import type { R3CaseFixture, R3FixtureQuestion } from '@reos/duediligence';
import { gateProgress } from './case-gates.js';
import { isSelfFetchableClaim, MANDATORY_GATES_BY_CASE_KIND } from './gate-catalog.js';
import type { GateCaseKind } from './case-subject.js';
import { openQuestionsSorted, type CaseDetail } from './dd-api.js';
import type { CaseTabId } from './texts.js';

const cs = { locale: 'cs' } as const;

type GateState = R3CaseFixture['gates'][number];

/**
 * Co se s oblasti da udelat.
 *  - `view`   — oblast je overena, akce je „Zobrazit" (detail v auditu),
 *  - `answer` — na oblast se vaze otevrena otazka, kterou makler zodpovi
 *               ve stavajicim panelu odpovedi (krok U1) → „Zadat",
 *  - `lookup` — REOS si doklad umi dotahnout sam → „Dohledat",
 *  - `open`   — chybi overit, otazka na to neni; vede do sekce auditu, kde
 *               se doklad doklada → „Zadat".
 */
export type ReviewAreaAction =
  | { readonly kind: 'view'; readonly tab: CaseTabId; readonly anchor: string | null }
  | { readonly kind: 'answer'; readonly questionId: string }
  | { readonly kind: 'lookup' }
  | { readonly kind: 'open'; readonly tab: CaseTabId; readonly anchor: string | null };

export interface ReviewArea {
  /** `claimType` oblasti — stabilni klic pro testid i katalog textu. */
  readonly claimType: string;
  readonly label: string;
  readonly verified: boolean;
  /** Druha radka: odkud to vime, nebo co k overení chybi. */
  readonly note: string;
  readonly action: ReviewAreaAction;
  readonly testId: string;
}

export interface CaseReviewAreas {
  readonly areas: readonly ReviewArea[];
  readonly verified: number;
  readonly total: number;
  /** Kolik oblasti umi REOS dohledat sam — nese to primarni akce karty. */
  readonly selfFetchable: number;
}

/**
 * Popisek oblasti pro MAKLERE.
 *
 * Zamerne NESDILENY s `CLAIM_TYPE_CAPTIONS` z `@reos/reports`: ten katalog
 * jde do KLIENTSKEHO PDF a je soucasti schvalene zlate regresni sady
 * (`pnpm test:golden`), takze zmena jeho textu meni dokumenty zakazniku.
 * Admin mluvi kratčeji a jinym registrem („Územní plán" vs. „Aplikovatelnost
 * regulativů"). Dve kopie tehoz POJMU, ale pro dve ruzna publika — proto
 * komentar u kodu misto sjednoceni (CLAUDE.md §8.2).
 */
function areaLabel(claimType: string): string {
  if (!KNOWN_AREAS.has(claimType)) return t('webAdmin.caseReview.areaFallback', cs);
  return t(`webAdmin.caseReview.area.${claimType}` as never, cs);
}

/** Oblasti, pro ktere ma admin vlastni kratky popisek i vetu „co chybi". */
const KNOWN_AREAS: ReadonlySet<string> = new Set([
  'ownership',
  'transaction_scope',
  'legal_access',
  'sewer_capacity',
  'easements',
  'liens',
  'building_legality',
  'structural_safety',
  'zoning_applicability',
  'utility_capacity',
  'realized_price',
  'professional_technical',
]);

/** Sekce auditu, kde se oblast doklada. */
const AREA_TAB: Readonly<Record<string, { readonly tab: CaseTabId; readonly anchor: string | null }>> = {
  legal_access: { tab: 'pristup', anchor: null },
  sewer_capacity: { tab: 'site', anchor: null },
  utility_capacity: { tab: 'site', anchor: null },
  zoning_applicability: { tab: 'uzemni-plan', anchor: null },
  realized_price: { tab: 'oceneni', anchor: null },
};
const AREA_TAB_DEFAULT: { readonly tab: CaseTabId; readonly anchor: string | null } = {
  tab: 'co-vime',
  anchor: null,
};

/**
 * Umi si REOS chybejici doklad teto oblasti dotahnout sam?
 *
 * Krok UXR5 bod 11: rozhoduje `isSelfFetchableClaim` (`gate-catalog.ts`) —
 * z behu, kdyz beh branu vyhodnotil, jinak z registru podle TYPU oblasti.
 * Do UXR5 se bez brany vracelo vzdy `false`, takze na ostrych datech (kde je
 * seznam bran prazdny, NALEZ 1 kroku UX1) nemela ani jedna oblast „Dohledat".
 */
function selfFetchable(claimType: string, gate: GateState | undefined): boolean {
  return isSelfFetchableClaim(claimType, gate?.closableBy);
}

/**
 * Otevrena otazka, kterou se oblast da ověřit.
 *
 * Vazba otazka → oblast NENI na otazce: nese ji seznam dalsich nejlepsich
 * kroku (`fixture.nba[].closesGateId` — tedy to, co o teze vazbe uz pocita
 * server, viz `trust/raise.ts`). Otazka se pak dohleda podle `questionId`.
 * Kdyz vazba chybi, oblast zadnou otazku nema a akce je „Zadat" do sekce
 * auditu — nic se nehada.
 */
function questionForArea(
  questions: readonly R3FixtureQuestion[],
  nba: readonly { readonly questionId: string; readonly closesGateId: string | null }[],
  gate: GateState | undefined,
): R3FixtureQuestion | undefined {
  if (gate === undefined) return undefined;
  const ids = new Set(nba.filter((item) => item.closesGateId === gate.gateId).map((item) => item.questionId));
  return questions.find((question) => ids.has(question.id));
}

export function caseReviewAreas(
  detail: CaseDetail,
  gateKind: GateCaseKind | null,
): CaseReviewAreas {
  const gates = detail.fixture.gates;
  const gateByClaimType = new Map(gates.map((gate) => [gate.claimType, gate]));
  const mandatory = gateKind === null ? [] : MANDATORY_GATES_BY_CASE_KIND[gateKind];
  // Poradi: nejdriv povinne oblasti v poradi registru (prvni neuzavrena je
  // ta, kterou jmenuje `decisionReadiness.reason`), pak zbytek, co beh
  // vyhodnotil. Zadne `sort()` — poradi registru je soucast smlouvy.
  const claimTypes = [
    ...mandatory,
    ...gates.map((gate) => gate.claimType).filter((claimType) => !mandatory.includes(claimType)),
  ];
  const openQuestions = openQuestionsSorted(detail);

  const areas = [...new Set(claimTypes)].map((claimType): ReviewArea => {
    const gate = gateByClaimType.get(claimType);
    const verified = gate !== undefined && gate.state === 'CLOSED';
    const place = AREA_TAB[claimType] ?? AREA_TAB_DEFAULT;
    const question = verified ? undefined : questionForArea(openQuestions, detail.fixture.nba, gate);
    const canLookup = !verified && selfFetchable(claimType, gate);

    const action: ReviewAreaAction = verified
      ? { kind: 'view', tab: place.tab, anchor: place.anchor }
      : canLookup
        ? { kind: 'lookup' }
        : question !== undefined
          ? { kind: 'answer', questionId: question.id }
          : { kind: 'open', tab: place.tab, anchor: place.anchor };

    const note = verified
      ? t('webAdmin.caseReview.noteVerified', cs)
      : canLookup
        ? t('webAdmin.caseReview.noteLookup', cs)
        : KNOWN_AREAS.has(claimType)
          ? t(`webAdmin.caseReview.missing.${claimType}` as never, cs)
          : t('webAdmin.caseReview.noteMissing', cs);

    return {
      claimType,
      label: areaLabel(claimType),
      verified,
      note,
      action,
      testId: `case-area-${claimType}`,
    };
  });

  /*
   * Jmenovatel i citatel jdou z `gateProgress` — stejne cislo, jake nese
   * prouzek fazi a hlavicka. Kdyz je seznam oblasti DELSI nez povinny
   * jmenovatel (beh vyhodnotil i nepovinne brany), plati delka seznamu:
   * jinak by karta rikala „2 ze 4 ověřeno" nad sesti radky.
   *
   * Krok D2: tohle je osa POKRYTI (kolik ze sjednaneho rozsahu je hotove),
   * NE stav „prověřeno". Slovo „prověřeno" smi rict jen `reviewed-state.ts`
   * a to jen nad POVINNYMI oblastmi; tenhle seznam je sirsi (nese i nepovinne
   * brany, ktere beh vyhodnotil), takze by jako stav lhal na obe strany.
   */
  const progress = gateProgress(detail, gateKind);
  const verifiedCount = areas.filter((area) => area.verified).length;
  const total = Math.max(progress.total, areas.length);
  return {
    areas,
    verified: Math.max(verifiedCount, Math.min(progress.closed, total)),
    total,
    selfFetchable: areas.filter((area) => area.action.kind === 'lookup').length,
  };
}

/**
 * Krok UXR5 bod 11 — „X ze Y ověřeno" NA JEDNOM MISTE.
 *
 * Tentyz retezec potrebuje karta „Prověření" i popisek kroku „Prověření" v
 * prouzku fazi. Do UXR5 si ho obe mista skladala sama ze stejneho klice —
 * dve kopie teze pravdy, ktere se mohly rozejit prvni zmenou formatu
 * (CLAUDE.md §8.2). Cislo uz jedno bylo (`caseReviewAreas`), ted je jedna i
 * jeho veta.
 */
export function reviewProgressText(review: CaseReviewAreas): string {
  return t('webAdmin.caseReview.progress', {
    ...cs,
    values: { done: String(review.verified), total: String(review.total) },
  });
}
